import React, { useState, useEffect, useMemo } from 'react';
import { Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { api } from '../../../shared/api';
import { useTranslation } from 'react-i18next';
import Select from '../../../components/form/AppSelect';
import AsyncCreatableSelect from '../../../components/form/AppAsyncCreatableSelect';
import { useCurrency } from '../../../shared/CurrencyContext';
import { useDateFormat } from '../../../shared/DateFormatContext';
import '../../../shared/ReactSelectDarkMode.css';
import { useQuery, useMutation } from '@tanstack/react-query';
import PageForm from '../../../components/layout/PageForm';
import PageSection from '../../../components/layout/PageSection';
import StatusBadge from '../../../components/ui/StatusBadge';

// Custom theme for React Select to support dark mode
const selectTheme = theme => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary: 'var(--tblr-primary)',
    primary75: 'var(--tblr-primary-rgb)',
    primary50: 'var(--tblr-primary-rgb)',
    primary25: 'var(--tblr-primary-rgb)',
    danger: 'var(--tblr-danger)',
    dangerLight: 'var(--tblr-danger-rgb)',
    neutral0: 'var(--tblr-bg-forms)',
    neutral5: 'var(--tblr-bg-surface)',
    neutral10: 'var(--tblr-bg-surface)',
    neutral20: 'var(--tblr-border-color)',
    neutral30: 'var(--tblr-border-color)',
    neutral40: 'var(--tblr-body-color-secondary)',
    neutral50: 'var(--tblr-body-color-secondary)',
    neutral60: 'var(--tblr-body-color-secondary)',
    neutral70: 'var(--tblr-body-color-secondary)',
    neutral80: 'var(--tblr-body-color)',
    neutral90: 'var(--tblr-body-color)'
  }
});

const ProjectInfo = ({ project, onUpdate }) => {
  const { t } = useTranslation();
  const { formatCurrency } = useCurrency();
  const { formatDate } = useDateFormat();
  const [isEditing, setIsEditing] = useState(false);
  const [focusField, setFocusField] = useState(null);
  const notSet = t('common.notSet');

  // Extract nested objects and prepare formData with IDs
  const [formData, setFormData] = useState({
    name: project.name,
    customer: project.customer?.id || null,
    description: project.description,
    location: project.location,
    start_date: project.start_date,
    end_date: project.end_date,
    budget: project.budget,
    billed_days: project.billed_days ?? 1,
    discount: project.discount ?? 0,
    tax_rate: project.tax_rate ?? 21,
    status: project.status?.id || null,
    type: project.type?.id || null,
    currency: project.currency?.id || null,
    processed_by: project.processed_by?.id || null
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedCustomerOption, setSelectedCustomerOption] = useState(
    project.customer?.id
      ? { value: project.customer.id, label: project.customer.company_name || project.customer.resp_person || `Customer #${project.customer.id}` }
      : null
  );
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const { currencies } = useCurrency();

  // Sync formData with project prop changes
  useEffect(() => {
    setFormData({
      name: project.name,
      customer: project.customer?.id || null,
      description: project.description,
      location: project.location,
      start_date: project.start_date,
      end_date: project.end_date,
      budget: project.budget,
      billed_days: project.billed_days ?? 1,
      discount: project.discount ?? 0,
      tax_rate: project.tax_rate ?? 21,
      status: project.status?.id || null,
      type: project.type?.id || null,
      currency: project.currency?.id || null,
      processed_by: project.processed_by?.id || null
    });
    if (project.customer?.id) {
      setSelectedCustomerOption({
        value: project.customer.id,
        label: project.customer.company_name || project.customer.resp_person || `Customer #${project.customer.id}`
      });
    } else {
      setSelectedCustomerOption(null);
    }
  }, [project]);

  const { data: editRefData } = useQuery({
    queryKey: ['project-info-edit-refs'],
    queryFn: async () => {
      const [statusesRes, typesRes, crewRes] = await Promise.all([
        api.get('projects/api/project-statuses/'),
        api.get('projects/api/project-types/'),
        api.get('crew/api/crew/'),
      ]);
      return { statuses: statusesRes, types: typesRes, crew: crewRes };
    },
    enabled: isEditing,
    staleTime: 5 * 60 * 1000,
  });

  const { data: costSummary } = useQuery({
    queryKey: ['project-cost-summary', project.id],
    queryFn: () => api.get(`projects/api/projects/${project.id}/cost-summary/`),
    enabled: isEditing,
    staleTime: 60 * 1000,
  });

  // Discount needed so that (grand_total × (1 − d/100)) = budget
  const grandTotal = costSummary ? parseFloat(costSummary.grand_total) : null;
  const suggestedDiscount = useMemo(() => {
    const budget = parseFloat(formData.budget);
    if (!grandTotal || !budget || budget <= 0 || grandTotal <= 0 || budget >= grandTotal) return null;
    return Math.max(0, Math.min(100, (1 - budget / grandTotal) * 100));
  }, [formData.budget, grandTotal]);

  const statusOptions = useMemo(
    () => (editRefData?.statuses || []).map(s => ({ value: s.id, label: s.name })),
    [editRefData]
  );
  const typeOptions = useMemo(
    () => (editRefData?.types || []).map(t => ({ value: t.id, label: t.name })),
    [editRefData]
  );
  const crewMemberOptions = useMemo(
    () => (editRefData?.crew || []).map(cm => ({
      value: cm.id,
      label: `${cm.company?.company_name || ''} | ${cm.display_name || ''}`
    })),
    [editRefData]
  );
  const currencyOptions = useMemo(
    () => (currencies || []).map(curr => ({ value: curr.id, label: `${curr.code} - ${curr.name}` })),
    [currencies]
  );

  const loadCustomerOptions = async (inputValue) => {
    try {
      const url = inputValue
        ? `customers/api/customers/?page_size=20&search=${encodeURIComponent(inputValue)}`
        : 'customers/api/customers/?page_size=20';
      const response = await api.get(url);
      return response.map(c => ({
        value: c.id,
        label: `${c.company_name || c.resp_person || 'Unknown'}${c.resp_person && c.company_name ? ` (${c.resp_person})` : ''}`
      }));
    } catch (err) {
      console.error('Error fetching customers:', err);
      return [];
    }
  };

  const handleCreateCustomer = async (inputValue) => {
    try {
      setCreatingCustomer(true);
      const response = await api.post('customers/api/customers/', { company_name: inputValue });
      const newOption = { value: response.id, label: response.company_name || inputValue };
      setSelectedCustomerOption(newOption);
      setFormData(prev => ({ ...prev, customer: response.id }));
    } catch (err) {
      console.error('Error creating customer:', err);
      setError(t('customers.failedToCreate', 'Failed to create customer'));
    } finally {
      setCreatingCustomer(false);
    }
  };

  const fetchStatuses = () => {}; // kept for compatibility — options from useQuery
  const fetchTypes = () => {};
  const fetchCrewMembers = () => {};

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const saveMutation = useMutation({
    mutationFn: (payload) => api.put(`projects/api/projects/${project.id}/`, payload),
    onSuccess: () => {
      setSuccess(t('projects.info.updateSuccess'));
      setIsEditing(false);
      setFocusField(null);
      onUpdate();
      setTimeout(() => setSuccess(null), 3000);
    },
    onError: (err) => {
      setError(t('projects.info.updateError'));
      console.error('Error updating project:', err);
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    const payload = {
      name: formData.name,
      customer: formData.customer,
      description: formData.description,
      location: formData.location,
      start_date: formData.start_date,
      end_date: formData.end_date,
      budget: formData.budget,
      billed_days: formData.billed_days,
      discount: formData.discount,
      tax_rate: formData.tax_rate,
      status: formData.status,
      type: formData.type,
      currency: formData.currency,
      processed_by: formData.processed_by || null
    };
    saveMutation.mutate(payload);
  };
  const loading = saveMutation.isPending; // replaces useState(false)

  const startEditField = (field) => { setFocusField(field); setIsEditing(true); };

  const renderDetailField = (label, value, field = null) => (
    <div
      onDoubleClick={field ? () => startEditField(field) : undefined}
      style={field ? { cursor: 'text' } : undefined}
      title={field ? t('common.doubleClickToEdit', 'Dvojklik pro úpravu') : undefined}
    >
      <small className="text-muted text-uppercase fw-semibold" style={{ letterSpacing: '0.06em', fontSize: '0.7rem' }}>{label}</small>
      <div className="fw-semibold mt-1">{value || notSet}</div>
    </div>
  );

  if (isEditing) {
    return (
      <PageForm
        title={t('projects.info.editTitle')}
        description={t('projects.info.editDescription')}
        onSubmit={handleSubmit}
        footer={(
          <>
            <Button variant="outline-secondary" onClick={() => { setIsEditing(false); setFocusField(null); }}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? t('common.saving') : t('common.save')}
            </Button>
          </>
        )}
      >
          {error && <Alert variant="danger" className="app-inline-alert mb-0">{error}</Alert>}

            {/* Row 1: Name + Customer */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('projects.projectName')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    autoFocus={focusField === 'name'}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <Form.Label className="mb-0">{t('projects.customer')}</Form.Label>
                    {selectedCustomerOption?.value && (
                      <a
                        href={`/customers/customer/${selectedCustomerOption.value}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-secondary"
                        title={t('common.openDetail')}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 6h-6a2 2 0 0 0 -2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-6" /><path d="M11 13l9 -9" /><path d="M15 4h5v5" /></svg>
                      </a>
                    )}
                  </div>
                  <AsyncCreatableSelect
                    name="customer"
                    value={selectedCustomerOption}
                    onChange={(option) => {
                      setSelectedCustomerOption(option);
                      setFormData(prev => ({ ...prev, customer: option?.value || null }));
                    }}
                    loadOptions={loadCustomerOptions}
                    defaultOptions
                    cacheOptions
                    onCreateOption={handleCreateCustomer}
                    isClearable
                    isLoading={creatingCustomer}
                    isDisabled={creatingCustomer}
                    placeholder={t('projects.selectCustomer')}
                    formatCreateLabel={(inputValue) => `+ ${inputValue}`}
                    noOptionsMessage={() => t('projects.noCustomers')}
                    theme={selectTheme}
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Row 2: Location + Status */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('projects.location')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="location"
                    value={formData.location || ''}
                    onChange={handleChange}
                    autoFocus={focusField === 'location'}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('projects.status')}</Form.Label>
                  <Select
                    name="status"
                    value={statusOptions.find(option => option.value === formData.status) || null}
                    onChange={(selectedOption) => {
                      const newStatusId = selectedOption ? selectedOption.value : null;
                      setFormData({ ...formData, status: newStatusId });
                    }}
                    options={statusOptions}
                    isClearable
                    isSearchable
                    placeholder={t('projects.selectStatus')}
                    theme={selectTheme}
                    noOptionsMessage={() => t('projects.noStatuses')}
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Row 3: Start date + End date + Processed by */}
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('projects.startDate')}</Form.Label>
                  <Form.Control
                    type="date"
                    name="start_date"
                    value={formData.start_date || ''}
                    onChange={handleChange}
                    autoFocus={focusField === 'start_date'}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('projects.endDate')}</Form.Label>
                  <Form.Control
                    type="date"
                    name="end_date"
                    value={formData.end_date || ''}
                    onChange={handleChange}
                    autoFocus={focusField === 'end_date'}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('projects.processedBy')}</Form.Label>
                  <Select
                    name="processed_by"
                    value={crewMemberOptions.find(o => o.value === formData.processed_by) || null}
                    onChange={(selectedOption) => {
                      setFormData({ ...formData, processed_by: selectedOption ? selectedOption.value : null });
                    }}
                    options={crewMemberOptions}
                    isClearable
                    isSearchable
                    placeholder={t('projects.selectProcessedBy')}
                    theme={selectTheme}
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Row 4: Type + Budget */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('projects.type')}</Form.Label>
                  <Select
                    name="type"
                    value={typeOptions.find(option => option.value === formData.type) || null}
                    onChange={(selectedOption) => {
                      const newTypeId = selectedOption ? selectedOption.value : null;
                      setFormData({ ...formData, type: newTypeId });
                    }}
                    options={typeOptions}
                    isClearable
                    isSearchable
                    placeholder={t('projects.selectType')}
                    theme={selectTheme}
                    noOptionsMessage={() => t('projects.noTypes')}
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('projects.budget')}</Form.Label>
                  <Form.Control
                    type="number"
                    name="budget"
                    value={formData.budget || ''}
                    onChange={handleChange}
                    autoFocus={focusField === 'budget'}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Row 5: Currency + Billed Days */}
            <Row>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('projects.currency')}</Form.Label>
                  <Select
                    name="currency"
                    value={currencyOptions.find(option => option.value === formData.currency) || null}
                    onChange={(selectedOption) => {
                      const newCurrencyId = selectedOption ? selectedOption.value : null;
                      setFormData({ ...formData, currency: newCurrencyId });
                    }}
                    options={currencyOptions}
                    isClearable
                    isSearchable
                    placeholder={t('projects.selectCurrency')}
                    theme={selectTheme}
                    noOptionsMessage={() => t('projects.noCurrencies')}
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                  <Form.Text className="text-muted">
                    {t('projects.currencyHelp')}
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('projects.billedDays')}</Form.Label>
                  <Form.Control
                    type="number"
                    name="billed_days"
                    min="0.01"
                    step="any"
                    value={formData.billed_days ?? 1}
                    onChange={handleChange}
                    autoFocus={focusField === 'billed_days'}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label className="d-flex align-items-center justify-content-between w-100">
                    <span>{t('projects.discount')} (%)</span>
                    {suggestedDiscount !== null && (
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 ms-2 text-decoration-none"
                        style={{ fontSize: '0.75rem' }}
                        onClick={() => setFormData(prev => ({ ...prev, discount: parseFloat(suggestedDiscount.toFixed(2)) }))}
                        title={t('projects.recalcDiscountTitle', 'Set discount so total matches budget')}
                      >
                        <i className="ti ti-calculator me-1" aria-hidden="true"></i>
                        {suggestedDiscount.toFixed(2)}%
                      </Button>
                    )}
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="discount"
                    min="0"
                    max="100"
                    step="any"
                    value={formData.discount ?? 0}
                    onChange={handleChange}
                    autoFocus={focusField === 'discount'}
                  />
                  {suggestedDiscount !== null && (
                    <Form.Text className="text-warning">
                      <i className="ti ti-alert-triangle me-1" aria-hidden="true"></i>
                      {t('projects.totalExceedsBudget', 'Total ({{total}}) exceeds budget', {
                        total: formatCurrency(grandTotal)
                      })}
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('projects.taxRate', 'Daňová sazba')} (%)</Form.Label>
                  <Form.Control
                    type="number"
                    name="tax_rate"
                    min="0"
                    max="100"
                    step="any"
                    value={formData.tax_rate ?? 21}
                    onChange={handleChange}
                    autoFocus={focusField === 'tax_rate'}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Row 6: Description */}
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('projects.description')}</Form.Label>
                  <Form.Control
                    as="textarea"
                    name="description"
                    value={formData.description || ''}
                    onChange={handleChange}
                    rows={3}
                    autoFocus={focusField === 'description'}
                  />
                </Form.Group>
              </Col>
            </Row>

      </PageForm>
    );
  }

  return (
    <PageSection
      title={t('projects.info.title')}
      description={t('projects.info.description')}
      actions={(
        <Button variant="primary" size="sm" onClick={() => setIsEditing(true)}>
          {t('common.edit')}
        </Button>
      )}
    >
        {success && <Alert variant="success" className="app-inline-alert">{success}</Alert>}

        <Row className="border-bottom py-3">
          <Col md={6}>
            {renderDetailField(t('projects.projectName'), project.name, 'name')}
          </Col>
          <Col md={6}>
            {renderDetailField(t('projects.customer'), project.customer ? (project.customer.company_name || project.customer.resp_person) : notSet, 'customer')}
          </Col>
        </Row>

        <Row className="border-bottom py-3">
          <Col md={6}>
            {renderDetailField(t('projects.location'), project.location, 'location')}
          </Col>
          <Col md={6} onDoubleClick={() => startEditField('status')} style={{ cursor: 'pointer' }} title={t('common.doubleClickToEdit', 'Dvojklik pro úpravu')}>
            <small className="text-muted text-uppercase fw-semibold" style={{ letterSpacing: '0.06em', fontSize: '0.7rem' }}>{t('projects.status')}</small>
            <div className="mt-1">
              <StatusBadge
                color={project.status?.color || '#6c7a91'}
                label={project.status?.name || notSet}
              />
            </div>
          </Col>
        </Row>

        <Row className="border-bottom py-3">
          <Col md={4}>
            {renderDetailField(t('projects.startDate'), project.start_date ? formatDate(project.start_date) : null, 'start_date')}
          </Col>
          <Col md={4}>
            {renderDetailField(t('projects.endDate'), project.end_date ? formatDate(project.end_date) : null, 'end_date')}
          </Col>
          <Col md={4}>
            {renderDetailField(t('projects.processedBy'), project.processed_by?.display_name, 'processed_by')}
          </Col>
        </Row>

        <Row className="border-bottom py-3">
          <Col md={6}>
            {renderDetailField(t('projects.type'), project.type?.name, 'type')}
          </Col>
          <Col md={6} onDoubleClick={() => startEditField('budget')} style={{ cursor: 'text' }} title={t('common.doubleClickToEdit', 'Dvojklik pro úpravu')}>
            <small className="text-muted text-uppercase fw-semibold" style={{ letterSpacing: '0.06em', fontSize: '0.7rem' }}>{t('projects.budget')}</small>
            <div className="fw-semibold mt-1">
              {project.budget
                ? (project.currency
                    ? formatCurrency(project.budget, { currency: project.currency })
                    : formatCurrency(project.budget))
                : notSet}
            </div>
          </Col>
        </Row>

        <Row className="border-bottom py-3">
          <Col md={3}>
            {renderDetailField(t('projects.currency'), project.currency ? `${project.currency.code} - ${project.currency.name}` : notSet, 'currency')}
          </Col>
          <Col md={3}>
            {renderDetailField(t('projects.billedDays'), String(project.billed_days ?? 1), 'billed_days')}
          </Col>
          <Col md={3}>
            {renderDetailField(t('projects.discount'), `${project.discount ?? 0} %`, 'discount')}
          </Col>
          <Col md={3}>
            {renderDetailField(t('projects.taxRate', 'Daňová sazba'), `${project.tax_rate ?? 21} %`, 'tax_rate')}
          </Col>
        </Row>

        <Row className="pt-3">
          <Col md={12} onDoubleClick={() => startEditField('description')} style={{ cursor: 'text' }} title={t('common.doubleClickToEdit', 'Dvojklik pro úpravu')}>
            <small className="text-muted text-uppercase fw-semibold" style={{ letterSpacing: '0.06em', fontSize: '0.7rem' }}>{t('projects.description')}</small>
            <div className="mt-1 text-secondary">{project.description || notSet}</div>
          </Col>
        </Row>
    </PageSection>
  );
};

export default ProjectInfo;
